<template>
  <v-dialog
    :value="value"
    max-width="640"
    scrollable
    content-class="address-builder-dialog"
    @input="$emit('input', $event)"
    @keydown.esc="close"
  >
    <v-card class="address-builder-card">
      <v-card-title class="address-builder-header pa-5 pb-4">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="40" class="mr-3">
            <v-icon dark>mdi-map-marker</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-medium">
              {{ $t("COMPANIES.ADDRESS_BUILDER_TITLE") }}
            </div>
            <div class="text-caption grey--text text--darken-1 mt-1">
              {{ $t("COMPANIES.ADDRESS_BUILDER_HINT") }}
            </div>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-5">
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-combobox
              v-model="localParts.country"
              :items="countryLabels"
              :label="$t('COMPANIES.ADDRESS_COUNTRY')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-web"
              clearable
            ></v-combobox>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="localParts.state"
              :label="$t('COMPANIES.ADDRESS_STATE')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-map"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="localParts.city"
              :label="$t('COMPANIES.ADDRESS_CITY')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-city"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="localParts.postal_code"
              :label="$t('COMPANIES.ADDRESS_POSTAL_CODE')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-inbox"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="localParts.street_line_1"
              :label="$t('COMPANIES.ADDRESS_STREET')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-home"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="localParts.street_line_2"
              :label="$t('COMPANIES.ADDRESS_STREET_2')"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-office-building"
              clearable
            ></v-text-field>
          </v-col>
        </v-row>

        <v-sheet
          class="address-builder-preview mt-5 pa-4 rounded-lg"
          color="grey lighten-4"
        >
          <div class="text-overline grey--text text--darken-1 mb-1">
            {{ $t("COMPANIES.ADDRESS_PREVIEW") }}
          </div>
          <div
            class="text-body-2"
            :class="{ 'grey--text text--lighten-1': !preview }"
          >
            {{ preview || $t("COMPANIES.ADDRESS_PREVIEW_EMPTY") }}
          </div>
        </v-sheet>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text color="grey darken-1" @click="resetParts">
          {{ $t("COMPANIES.ADDRESS_CLEAR") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t("GENERAL.LABELS.CLOSE") }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!preview"
          @click="apply"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ $t("COMPANIES.ADDRESS_APPLY") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const EMPTY_PARTS = () => ({
  street_line_1: "",
  street_line_2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
});

const PART_KEYS = [
  "country",
  "city",
  "state",
  "postal_code",
  "street_line_1",
  "street_line_2",
];

export function buildAddressFromParts(parts) {
  return PART_KEYS
    .map((key) => (parts[key] || "").toString().trim())
    .filter(Boolean)
    .join(", ");
}

export function cloneAddressParts(parts = {}) {
  return {
    ...EMPTY_PARTS(),
    ...Object.keys(EMPTY_PARTS()).reduce((result, key) => {
      result[key] = parts[key] || "";
      return result;
    }, {}),
  };
}

export default {
  name: "AddressBuilderDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    parts: {
      type: Object,
      default: () => EMPTY_PARTS(),
    },
    countries: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localParts: EMPTY_PARTS(),
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.localParts = cloneAddressParts(this.parts);
      }
    },
  },
  computed: {
    countryLabels() {
      return this.countries
        .map((country) => country.label)
        .filter(Boolean);
    },
    preview() {
      return buildAddressFromParts(this.localParts);
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    resetParts() {
      this.localParts = EMPTY_PARTS();
    },
    apply() {
      if (!this.preview) {
        return;
      }

      this.$emit("apply", {
        address: this.preview,
        parts: cloneAddressParts(this.localParts),
      });
      this.close();
    },
  },
};
</script>

<style scoped>
.address-builder-header {
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
}

.address-builder-preview {
  border: 1px dashed rgba(0, 0, 0, 0.12);
}
</style>
